/* =====================================================================================
   Forecast.

   Cash-flow and P&L scenario modelling: distributor / direct / profit-share revenue
   arrangements as typed fields rather than free-text opex lines, non-linear growth
   curves (the rate can change every period), product lines that start and retire on
   their own schedule, and people cost driven by headcount so the two can never drift
   apart the way a flat "People" opex line always eventually does.

   PEOPLE COST CAN BE LIVE. Headcount and cost-per-head are editable on every scenario,
   the same as everything else here, but a period can also be pulled from Crew's own
   records via app.crew_headcount_cost_by_period, an aggregate-only function that never
   returns a name, a department or an individual salary (hr stays walled; see 0097). A
   pulled period is marked live and is overwritten by the next pull, not silently frozen.

   WHAT THIS DOES NOT KNOW. Forum's ledger is quote-to-cash (what has actually been
   invoiced and paid); it has no supplier-bill or purchase-invoice table at all, and the
   Sage push is one-way outbound. So this module cannot show "actual opex" against the
   plan: only the plan itself, plus live headcount cost. Every other line is what the
   person building the scenario says it is, same as the standalone tool it was ported
   from.
   ===================================================================================== */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { BACKEND } from '../lib/db';
import { TENANT } from '@tenant';
import {
  type CaseType, type Arrangement, type Scenario, type Channel, type OpexLine, type EquityRaise,
  type ComputedModel, CASE_LABEL, ARR_LABEL, volumeAt, computeModel, breakEvenYear,
} from '../lib/glidepath';
import './forecast.css';

/* Types and the compute engine (volumeAt, computeChannel, computeModel, breakEvenYear) now
   live in ../lib/glidepath, shared with Council so a seat reviews the same arithmetic this
   module shows, not a second copy of it. See that file's own header for why. */

/* Uniform price/volume flex across every channel, for the sensitivity grid. Kept separate
   from computeModel rather than adding optional multipliers there: this path is never
   persisted and never touches the real scenario, only ever a what-if. */
function computeUniform(sc: Scenario, channels: Channel[], opex: OpexLine[], equity: EquityRaise[], priceMul: number, volMul: number) {
  const N = sc.years.length;
  const totRev: number[] = [], ebitda: number[] = [], cash: number[] = [];
  const perChannelRC = channels.map((ch) => {
    const rev: number[] = [], cogs: number[] = [];
    for (let i = 0; i < N; i++) {
      const u = volumeAt(ch, i) * volMul;
      const gross = u * ch.price * priceMul;
      const share = gross * (Number(ch.rev_share_pct) || 0) / 100;
      rev.push(gross - share); cogs.push(u * ch.cash_cost);
    }
    return { rev, cogs };
  });
  for (let i = 0; i < N; i++) {
    let r = 0, c = 0;
    perChannelRC.forEach((p) => { r += p.rev[i]; c += p.cogs[i]; });
    totRev.push(r);
    const people = (Number(sc.headcount[i]) || 0) * (Number(sc.cost_per_head[i]) || 0);
    let ox = people; opex.forEach((o) => { ox += Number(o.amounts[i]) || 0; });
    ebitda.push((r - c) + (Number(sc.grants_by_period[i]) || 0) - ox);
  }
  const equityByPeriod = Array(N).fill(0);
  equity.forEach((e) => { if (e.period_idx >= 0 && e.period_idx < N) equityByPeriod[e.period_idx] += Number(e.amount) || 0; });
  let running = 0;
  for (let i = 0; i < N; i++) { running += ebitda[i] + equityByPeriod[i]; cash.push(running); }
  return { totRev, ebitda, cash };
}
function metricValue(m: { totRev: number[]; ebitda: number[]; cash: number[] }, key: 'ebitda' | 'totRev' | 'cash', yi: number) {
  return m[key][yi];
}

/* Flex ONE channel's price or volume, every other channel held at base. Used for the
   per-channel tornado, so a founder can see which single arrangement the outcome is
   most sensitive to rather than only the blended, everything-at-once view. */
function computeSingleChannelFlex(sc: Scenario, channels: Channel[], opex: OpexLine[], equity: EquityRaise[], channelId: string, field: 'price' | 'vol', mul: number) {
  const N = sc.years.length;
  const totRev: number[] = [], ebitda: number[] = [], cash: number[] = [];
  const perChannelRC = channels.map((ch) => {
    const priceMul = ch.id === channelId && field === 'price' ? mul : 1;
    const volMul = ch.id === channelId && field === 'vol' ? mul : 1;
    const rev: number[] = [], cogs: number[] = [];
    for (let i = 0; i < N; i++) {
      const u = volumeAt(ch, i) * volMul;
      const gross = u * ch.price * priceMul;
      const share = gross * (Number(ch.rev_share_pct) || 0) / 100;
      rev.push(gross - share); cogs.push(u * ch.cash_cost);
    }
    return { rev, cogs };
  });
  for (let i = 0; i < N; i++) {
    let r = 0, c = 0;
    perChannelRC.forEach((p) => { r += p.rev[i]; c += p.cogs[i]; });
    totRev.push(r);
    const people = (Number(sc.headcount[i]) || 0) * (Number(sc.cost_per_head[i]) || 0);
    let ox = people; opex.forEach((o) => { ox += Number(o.amounts[i]) || 0; });
    ebitda.push((r - c) + (Number(sc.grants_by_period[i]) || 0) - ox);
  }
  const equityByPeriod = Array(N).fill(0);
  equity.forEach((e) => { if (e.period_idx >= 0 && e.period_idx < N) equityByPeriod[e.period_idx] += Number(e.amount) || 0; });
  let running = 0;
  for (let i = 0; i < N; i++) { running += ebitda[i] + equityByPeriod[i]; cash.push(running); }
  return { totRev, ebitda, cash };
}
function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * mag;
}

/* ---------------- formatting ---------------- */

const BASE_CCY = TENANT.brand.baseCurrency ?? 'GBP';
function fmt(sc: Scenario, n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: sc.currency || BASE_CCY, maximumFractionDigits: 0 }).format(n);
}
function fmtCompact(sc: Scenario, n: number): string {
  const neg = n < 0; const abs = Math.abs(n);
  const symbol = new Intl.NumberFormat('en-GB', { style: 'currency', currency: sc.currency || BASE_CCY }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? sc.currency;
  let s: string;
  if (abs >= 1e6) s = (abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  else if (abs >= 1e3) s = (abs / 1e3).toFixed(0) + 'k';
  else s = Math.round(abs).toString();
  return (neg ? '−' : '') + symbol + s;
}
const emptyGrowth = (n: number) => ({ base: 0, rates: Array(n).fill(0) });

/* ---------------- data access ---------------- */

async function loadScenarios(): Promise<Scenario[]> {
  if (BACKEND !== 'supabase') return [];
  const { supabase } = await import('../lib/supabase');
  const { data, error } = await supabase().schema('finance').from('scenarios').select('*').order('created_at');
  if (error) throw error;
  return (data ?? []) as Scenario[];
}
async function loadChildren(scenarioId: string) {
  const { supabase } = await import('../lib/supabase');
  const fin = supabase().schema('finance');
  const [{ data: channels, error: e1 }, { data: opex, error: e2 }, { data: equity, error: e3 }] = await Promise.all([
    fin.from('channels').select('*').eq('scenario_id', scenarioId).order('sort_order'),
    fin.from('opex_lines').select('*').eq('scenario_id', scenarioId).order('sort_order'),
    fin.from('equity_raises').select('*').eq('scenario_id', scenarioId).order('period_idx'),
  ]);
  if (e1) throw e1; if (e2) throw e2; if (e3) throw e3;
  return { channels: (channels ?? []) as Channel[], opex: (opex ?? []) as OpexLine[], equity: (equity ?? []) as EquityRaise[] };
}

export function ForecastView() {
  const [scenarios, setScenarios] = useState<Scenario[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [opex, setOpex] = useState<OpexLine[]>([]);
  const [equity, setEquity] = useState<EquityRaise[]>([]);
  const [section, setSection] = useState<'overview' | 'channels' | 'costs' | 'pl' | 'sensitivity' | 'compare'>('overview');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [pullDate, setPullDate] = useState<Record<number, string>>({});
  // Ephemeral, view-only: never written to the database. Turning a section "on" swaps what
  // computeModel sees for that section to the base case's data; turning it off reverts
  // automatically, since the real edited state was never touched in the first place.
  const [sectionOverride, setSectionOverride] = useState<Set<string>>(new Set());
  const [sensMetric, setSensMetric] = useState<'ebitda' | 'totRev' | 'cash'>('ebitda');
  const [sensYearIdx, setSensYearIdx] = useState<number | null>(null);
  const [childrenCache, setChildrenCache] = useState<Record<string, { channels: Channel[]; opex: OpexLine[]; equity: EquityRaise[] }>>({});
  const [printAt, setPrintAt] = useState<number>(0);

  const active = scenarios?.find((s) => s.id === activeId) ?? null;
  const baseScenarioObj = scenarios?.find((s) => s.case_type === 'base') ?? null;
  const baseChildren = baseScenarioObj ? childrenCache[baseScenarioObj.id] : null;

  const reloadChildren = useCallback(async (scenarioId: string) => {
    const { channels: c, opex: o, equity: e } = await loadChildren(scenarioId);
    setChannels(c); setOpex(o); setEquity(e);
    setChildrenCache((cache) => ({ ...cache, [scenarioId]: { channels: c, opex: o, equity: e } }));
  }, []);

  const ensureChildrenLoaded = useCallback(async (scenarioId: string) => {
    if (childrenCache[scenarioId]) return childrenCache[scenarioId];
    const kids = await loadChildren(scenarioId);
    setChildrenCache((cache) => ({ ...cache, [scenarioId]: kids }));
    return kids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenCache]);

  // The Compare tab and the section-toggle both need every scenario's children, not just
  // the active one's. Load whatever is missing whenever the scenario list changes or the
  // Compare tab opens, rather than blocking the initial page load on all of them upfront.
  useEffect(() => {
    if (!scenarios) return;
    if (section !== 'compare' && !sectionOverride.size) return;
    scenarios.forEach((s) => { if (!childrenCache[s.id]) ensureChildrenLoaded(s.id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios, section, sectionOverride]);

  function toggleSection(key: string) {
    if (!baseScenarioObj) { flash('Tag a scenario as Base case first, in Compare scenarios'); return; }
    if (active?.case_type === 'base') { flash('This is the base case'); return; }
    setSectionOverride((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  useEffect(() => {
    (async () => {
      try {
        const s = await loadScenarios();
        setScenarios(s);
        if (s.length) { setActiveId(s[0].id); await reloadChildren(s[0].id); }
      } catch (err: any) { setMsg(err.message ?? 'Could not load scenarios'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchScenario = useCallback(async (id: string) => {
    setActiveId(id);
    setBusy(true);
    try { await reloadChildren(id); } catch (err: any) { setMsg(err.message); } finally { setBusy(false); }
  }, [reloadChildren]);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };

  async function createScenario() {
    const { supabase } = await import('../lib/supabase');
    const years = ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'];
    const { data, error } = await supabase().schema('finance').from('scenarios').insert({
      name: 'Untitled scenario', case_type: 'other', currency: BASE_CCY, unit_label: 'units',
      years, grants_by_period: Array(6).fill(0), headcount: Array(6).fill(0), cost_per_head: Array(6).fill(0),
    }).select('*').single();
    if (error) { flash(error.message); return; }
    setScenarios((s) => [...(s ?? []), data as Scenario]);
    await switchScenario((data as Scenario).id);
    flash('New scenario created');
  }

  async function duplicateScenario() {
    if (!active) return;
    const { supabase } = await import('../lib/supabase');
    const fin = supabase().schema('finance');
    const { id: _drop, ...rest } = active as any;
    const { data: copy, error } = await fin.from('scenarios').insert({ ...rest, name: active.name + ' (copy)' }).select('*').single();
    if (error) { flash(error.message); return; }
    const copyId = (copy as Scenario).id;
    await Promise.all([
      ...channels.map(({ id: _i, scenario_id: _s, ...c }) => fin.from('channels').insert({ ...c, scenario_id: copyId })),
      ...opex.map(({ id: _i, scenario_id: _s, ...o }) => fin.from('opex_lines').insert({ ...o, scenario_id: copyId })),
      ...equity.map(({ id: _i, scenario_id: _s, ...e }) => fin.from('equity_raises').insert({ ...e, scenario_id: copyId })),
    ]);
    setScenarios((s) => [...(s ?? []), copy as Scenario]);
    await switchScenario(copyId);
    flash('Scenario duplicated');
  }

  async function deleteScenario() {
    if (!active) return;
    if (scenarios && scenarios.length <= 1) { flash('At least one scenario must remain'); return; }
    if (!confirm(`Delete "${active.name}"? This cannot be undone.`)) return;
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('finance').from('scenarios').delete().eq('id', active.id);
    if (error) { flash(error.message); return; }
    const rest = (scenarios ?? []).filter((s) => s.id !== active.id);
    setScenarios(rest);
    if (rest.length) await switchScenario(rest[0].id);
    flash('Scenario deleted');
  }

  async function patchScenario(patch: Partial<Scenario>) {
    if (!active) return;
    setScenarios((s) => (s ?? []).map((x) => (x.id === active.id ? { ...x, ...patch } : x)));
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('finance').from('scenarios').update(patch).eq('id', active.id);
    if (error) flash(error.message);
  }

  function setArrayField(field: 'grants_by_period' | 'headcount' | 'cost_per_head', idx: number, val: number) {
    if (!active) return;
    const next = [...active[field]]; next[idx] = val;
    patchScenario({ [field]: next } as Partial<Scenario>);
  }

  async function patchScenarioById(id: string, patch: Partial<Scenario>) {
    setScenarios((s) => (s ?? []).map((x) => (x.id === id ? { ...x, ...patch } : x)));
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('finance').from('scenarios').update(patch).eq('id', id);
    if (error) flash(error.message);
  }

  /* Copies the base case (channels, opex, equity, people) into a new scenario with every
     channel's volume scaled +/-20% from the second period onward, price/cost/opex/grants/
     equity unchanged, matching the same convention as the standalone tool. */
  async function deriveScenario(kind: 'upside' | 'downside') {
    const base = baseScenarioObj;
    if (!base) { flash('Tag a scenario as Base case first'); return; }
    const baseKids = await ensureChildrenLoaded(base.id);
    const factor = kind === 'upside' ? 1.2 : 0.8;
    const { supabase } = await import('../lib/supabase');
    const fin = supabase().schema('finance');
    const { id: _drop, ...rest } = base as any;
    const strippedName = base.name.replace(/\s*[-–]\s*(base case|upside|downside).*/i, '');
    const { data: copy, error } = await fin.from('scenarios').insert({
      ...rest, name: `${strippedName} - ${kind === 'upside' ? 'Upside' : 'Downside'} case`, case_type: kind,
      notes: `Derived from "${base.name}": every channel's volume scaled ${kind === 'upside' ? 'up' : 'down'} 20% from the second modelled period onward. Price, cost, opex, grants and equity unchanged. Edit freely from here.`,
    }).select('*').single();
    if (error) { flash(error.message); return; }
    const copyId = (copy as Scenario).id;
    await Promise.all([
      ...baseKids.channels.map(({ id: _i, scenario_id: _s, manual_volumes, growth, ...c }) => fin.from('channels').insert({
        ...c, scenario_id: copyId,
        manual_volumes: manual_volumes.map((v, i) => (i === 0 ? v : Math.round(v * factor))),
        growth: { ...growth, base: Math.round((growth?.base ?? 0) * factor) },
      })),
      ...baseKids.opex.map(({ id: _i, scenario_id: _s, ...o }) => fin.from('opex_lines').insert({ ...o, scenario_id: copyId })),
      ...baseKids.equity.map(({ id: _i, scenario_id: _s, ...e }) => fin.from('equity_raises').insert({ ...e, scenario_id: copyId })),
    ]);
    setScenarios((s) => [...(s ?? []), copy as Scenario]);
    setSection('compare');
    flash(`${kind === 'upside' ? 'Upside' : 'Downside'} case created from ${base.name}`);
  }

  async function pullLiveHeadcount(idx: number) {
    if (!active) return;
    const asOf = pullDate[idx] || new Date().toISOString().slice(0, 10);
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase().schema('app').rpc('crew_headcount_cost_by_period', { p_as_of: asOf });
    if (error) { flash(error.message); return; }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) { flash('No data returned'); return; }
    const hc = [...active.headcount]; hc[idx] = row.headcount;
    const cph = [...active.cost_per_head]; cph[idx] = row.headcount ? Math.round(row.cost_total / row.headcount) : 0;
    await patchScenario({ headcount: hc, cost_per_head: cph, headcount_is_live: true });
    flash(`Pulled from Crew as of ${asOf}: ${row.headcount} active, ${fmt(active, row.cost_total)}`);
  }

  /* ---------------- channel CRUD ---------------- */

  async function addChannel() {
    if (!active) return;
    const { supabase } = await import('../lib/supabase');
    const N = active.years.length;
    const { data, error } = await supabase().schema('finance').from('channels').insert({
      scenario_id: active.id, name: 'New channel', arrangement: 'direct', price: 0, cash_cost: 0,
      rev_share_pct: 0, start_idx: 0, vol_mode: 'manual', manual_volumes: Array(N).fill(0),
      growth: emptyGrowth(N), sort_order: channels.length,
    }).select('*').single();
    if (error) { flash(error.message); return; }
    setChannels((c) => [...c, data as Channel]);
  }
  async function updateChannel(id: string, patch: Partial<Channel>) {
    setChannels((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('finance').from('channels').update(patch).eq('id', id);
    if (error) flash(error.message);
  }
  async function removeChannel(id: string) {
    if (!confirm('Remove this channel?')) return;
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('finance').from('channels').delete().eq('id', id);
    if (error) { flash(error.message); return; }
    setChannels((cs) => cs.filter((c) => c.id !== id));
  }

  /* ---------------- opex CRUD ---------------- */

  async function addOpexLine() {
    if (!active) return;
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase().schema('finance').from('opex_lines').insert({
      scenario_id: active.id, name: 'New category', amounts: Array(active.years.length).fill(0), sort_order: opex.length,
    }).select('*').single();
    if (error) { flash(error.message); return; }
    setOpex((o) => [...o, data as OpexLine]);
  }
  async function updateOpexLine(id: string, patch: Partial<OpexLine>) {
    setOpex((os) => os.map((o) => (o.id === id ? { ...o, ...patch } : o)));
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('finance').from('opex_lines').update(patch).eq('id', id);
    if (error) flash(error.message);
  }
  async function removeOpexLine(id: string) {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('finance').from('opex_lines').delete().eq('id', id);
    if (error) { flash(error.message); return; }
    setOpex((os) => os.filter((o) => o.id !== id));
  }

  /* ---------------- equity CRUD ---------------- */

  async function addEquity() {
    if (!active) return;
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase().schema('finance').from('equity_raises').insert({
      scenario_id: active.id, name: 'New round', period_idx: 0, amount: 0,
    }).select('*').single();
    if (error) { flash(error.message); return; }
    setEquity((e) => [...e, data as EquityRaise]);
  }
  async function updateEquity(id: string, patch: Partial<EquityRaise>) {
    setEquity((es) => es.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('finance').from('equity_raises').update(patch).eq('id', id);
    if (error) flash(error.message);
  }
  async function removeEquity(id: string) {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase().schema('finance').from('equity_raises').delete().eq('id', id);
    if (error) { flash(error.message); return; }
    setEquity((es) => es.filter((e) => e.id !== id));
  }

  // Effective inputs: real edited state, except a card whose section is toggled to "compare
  // to base case" reads the base scenario's data for that one section only. Nothing here is
  // ever written back, so switching the toggle off always lands exactly where editing left off.
  const canOverride = !!baseScenarioObj && active?.case_type !== 'base' && !!baseChildren;
  const effChannels = canOverride && sectionOverride.has('channels') ? baseChildren!.channels : channels;
  const effOpex = canOverride && sectionOverride.has('opex') ? baseChildren!.opex : opex;
  const effEquity = canOverride && sectionOverride.has('equity') ? baseChildren!.equity : equity;
  const effScenario = useMemo(() => {
    if (!active) return null;
    if (!canOverride || (!sectionOverride.has('grants') && !sectionOverride.has('people'))) return active;
    return {
      ...active,
      grants_by_period: sectionOverride.has('grants') ? baseScenarioObj!.grants_by_period : active.grants_by_period,
      headcount: sectionOverride.has('people') ? baseScenarioObj!.headcount : active.headcount,
      cost_per_head: sectionOverride.has('people') ? baseScenarioObj!.cost_per_head : active.cost_per_head,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, canOverride, sectionOverride, baseScenarioObj]);

  const res = useMemo(() => (effScenario ? computeModel(effScenario, effChannels, effOpex, effEquity) : null),
    [effScenario, effChannels, effOpex, effEquity]);

  if (scenarios === null) return <div className="fc-loading">Loading forecasts…</div>;
  if (BACKEND !== 'supabase') return <div className="card fc-empty">Forecast needs a connected backend. Set VITE_SUPABASE_URL to use it.</div>;

  return (
    <div className="fc">
      <div className="fc-head fc-noprint">
        <div>
          <h2>Glidepath</h2>
          <p className="fc-sub">Model how each revenue channel is actually arranged, and see the P&amp;L recalculate as you change any input.</p>
        </div>
        <div className="fc-scenario-bar">
          <select className="input fc-scenario-select" value={activeId ?? ''} disabled={busy}
            onChange={(e) => switchScenario(e.target.value)}>
            {(scenarios ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button className="btn sm" onClick={createScenario} disabled={busy}>+ New</button>
          <button className="btn sm" onClick={duplicateScenario} disabled={busy || !active}>Duplicate</button>
          <button className="btn sm" onClick={deleteScenario} disabled={busy || !active}>Delete</button>
          <button className="btn primary sm" onClick={() => { setPrintAt(Date.now()); setTimeout(() => window.print(), 50); }} disabled={!active}>Print / Export PDF</button>
          {busy && <span className="fc-hint">Loading…</span>}
        </div>
      </div>

      {msg && <div className="fc-toast fc-noprint">{msg}</div>}

      {!active ? (
        <div className="card fc-empty">No scenarios yet. Create one to start modelling.</div>
      ) : (
        <>
          <div className="fc-tabs fc-noprint">
            {(['overview', 'channels', 'costs', 'pl', 'sensitivity', 'compare'] as const).map((k) => (
              <button key={k} className={`fc-tab ${section === k ? 'active' : ''}`} onClick={() => setSection(k)}>
                {k === 'overview' ? 'Overview' : k === 'channels' ? 'Revenue channels' : k === 'costs' ? 'Costs & funding'
                  : k === 'pl' ? 'P&L' : k === 'sensitivity' ? 'Sensitivity' : 'Compare scenarios'}
              </button>
            ))}
          </div>

          <div className="fc-noprint">
            {section === 'overview' && res && (
              <OverviewSection active={active} res={res} channels={effChannels} patchScenario={patchScenario} />
            )}
            {section === 'channels' && (
              <ChannelsSection scenario={active} channels={effChannels} addChannel={addChannel}
                updateChannel={updateChannel} removeChannel={removeChannel}
                overridden={canOverride && sectionOverride.has('channels')} onToggle={() => toggleSection('channels')} canToggle={canOverride} />
            )}
            {section === 'costs' && (
              <CostsSection scenario={effScenario!} opex={effOpex} equity={effEquity}
                addOpexLine={addOpexLine} updateOpexLine={updateOpexLine} removeOpexLine={removeOpexLine}
                addEquity={addEquity} updateEquity={updateEquity} removeEquity={removeEquity}
                setArrayField={setArrayField} pullDate={pullDate} setPullDate={setPullDate}
                pullLiveHeadcount={pullLiveHeadcount} patchScenario={patchScenario}
                canToggle={canOverride} sectionOverride={sectionOverride} onToggle={toggleSection} />
            )}
            {section === 'pl' && res && <PLSection scenario={effScenario!} channels={effChannels} opex={effOpex} res={res} />}
            {section === 'sensitivity' && (
              <SensitivitySection scenario={active} channels={channels} opex={opex} equity={equity}
                metric={sensMetric} setMetric={setSensMetric} yearIdx={sensYearIdx ?? active.years.length - 1} setYearIdx={setSensYearIdx} />
            )}
            {section === 'compare' && (
              <CompareSection scenarios={scenarios} childrenCache={childrenCache} ensureChildrenLoaded={ensureChildrenLoaded}
                patchScenarioById={patchScenarioById} deriveScenario={deriveScenario} />
            )}
          </div>

          {res && <PrintReport key={printAt} scenario={effScenario!} channels={effChannels} opex={effOpex} res={res} />}
        </>
      )}
    </div>
  );
}

/* ---------------- Overview ---------------- */

function OverviewSection({ active, res, channels, patchScenario }: {
  active: Scenario; res: ComputedModel; channels: Channel[];
  patchScenario: (p: Partial<Scenario>) => void;
}) {
  const N = active.years.length;
  return (
    <div className="fc-section">
      <div className="fc-stat-row">
        <div className="card fc-stat">
          <div className="label">Revenue, {active.years[N - 1]}</div>
          <div className="fc-stat-val mono">{fmtCompact(active, res.totRev[N - 1])}</div>
        </div>
        <div className="card fc-stat">
          <div className="label">EBITDA, {active.years[N - 1]}</div>
          <div className={`fc-stat-val mono ${res.ebitda[N - 1] < 0 ? 'fc-neg' : 'fc-pos'}`}>{fmtCompact(active, res.ebitda[N - 1])}</div>
        </div>
        <div className="card fc-stat">
          <div className="label">Break-even</div>
          <div className="fc-stat-val">{breakEvenYear(active, res)}</div>
        </div>
        <div className="card fc-stat">
          <div className="label">Cash low point</div>
          <div className={`fc-stat-val mono ${res.minCash < 0 ? 'fc-neg' : ''}`}>{fmtCompact(active, res.minCash)}</div>
          <div className="fc-stat-sub">{active.years[res.minCashIdx]}</div>
        </div>
      </div>

      <div className="card fc-card">
        <h3>Revenue by channel</h3>
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Channel</th>{active.years.map((y) => <th key={y} className="num">{y}</th>)}</tr></thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.id}><td>{c.name}</td>{res.perChannel[c.id].rev.map((v, i) => <td key={i} className="num mono">{fmt(active, v)}</td>)}</tr>
              ))}
              <tr className="fc-total"><td>Total revenue</td>{res.totRev.map((v, i) => <td key={i} className="num mono">{fmt(active, v)}</td>)}</tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card fc-card">
        <div className="fc-field">
          <label className="label">Scenario name</label>
          <input className="input" value={active.name} onChange={(e) => patchScenario({ name: e.target.value })} />
        </div>
        <div className="fc-field">
          <label className="label">Case</label>
          <select className="input" value={active.case_type} onChange={(e) => patchScenario({ case_type: e.target.value as CaseType })}>
            {(Object.keys(CASE_LABEL) as CaseType[]).map((k) => <option key={k} value={k}>{CASE_LABEL[k]}</option>)}
          </select>
        </div>
        <div className="fc-field">
          <label className="label">Notes</label>
          <textarea className="input fc-notes" value={active.notes ?? ''} onChange={(e) => patchScenario({ notes: e.target.value })} rows={2} />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Channels ---------------- */

function SectionToggleBtn({ canToggle, on, onClick }: { canToggle: boolean; on: boolean; onClick: () => void }) {
  if (!canToggle) return null;
  return <button className={`btn ghost sm fc-toggle ${on ? 'active' : ''}`} onClick={onClick}>
    {on ? '← Back to own assumptions' : 'Compare to base case'}
  </button>;
}

function ChannelsSection({ scenario, channels, addChannel, updateChannel, removeChannel, overridden, onToggle, canToggle }: {
  scenario: Scenario; channels: Channel[];
  addChannel: () => void; updateChannel: (id: string, p: Partial<Channel>) => void; removeChannel: (id: string) => void;
  overridden: boolean; onToggle: () => void; canToggle: boolean;
}) {
  return (
    <div className="fc-section">
      <div className="fc-section-head">
        <p className="fc-sub">One row per way you sell: direct, through a distributor, or a profit-share arrangement. Each can start and retire on its own schedule.</p>
        <div className="fc-btn-row">
          <SectionToggleBtn canToggle={canToggle} on={overridden} onClick={onToggle} />
          <button className="btn primary sm" onClick={addChannel} disabled={overridden}>+ Add channel</button>
        </div>
      </div>
      {overridden && <div className="fc-override-note">Showing the base case's revenue channels, read only. Switch back to edit your own.</div>}
      {channels.length === 0 && <div className="card fc-empty">No revenue channels yet.</div>}
      <div className={overridden ? 'fc-readonly' : ''}>
        {channels.map((c) => <ChannelCard key={c.id} scenario={scenario} channel={c} updateChannel={updateChannel} removeChannel={removeChannel} />)}
      </div>
    </div>
  );
}

function ChannelCard({ scenario, channel, updateChannel, removeChannel }: {
  scenario: Scenario; channel: Channel;
  updateChannel: (id: string, p: Partial<Channel>) => void; removeChannel: (id: string) => void;
}) {
  const c = channel;
  return (
    <div className="card fc-channel">
      <div className="fc-channel-head">
        <input className="input fc-name-input" value={c.name} onChange={(e) => updateChannel(c.id, { name: e.target.value })} />
        <span className="pill accent">{ARR_LABEL[c.arrangement]}</span>
        <button className="btn ghost sm" onClick={() => removeChannel(c.id)}>Remove</button>
      </div>
      <div className="fc-field-grid">
        <div className="fc-field">
          <label className="label">Arrangement</label>
          <select className="input" value={c.arrangement} onChange={(e) => updateChannel(c.id, { arrangement: e.target.value as Arrangement })}>
            {(Object.keys(ARR_LABEL) as Arrangement[]).map((k) => <option key={k} value={k}>{ARR_LABEL[k]}</option>)}
          </select>
        </div>
        <div className="fc-field"><label className="label">Price / unit</label>
          <input className="input mono" type="number" value={c.price} onChange={(e) => updateChannel(c.id, { price: Number(e.target.value) || 0 })} /></div>
        <div className="fc-field"><label className="label">Cash cost / unit</label>
          <input className="input mono" type="number" value={c.cash_cost} onChange={(e) => updateChannel(c.id, { cash_cost: Number(e.target.value) || 0 })} /></div>
        <div className="fc-field"><label className="label">Share paid away %</label>
          <input className="input mono" type="number" disabled={c.arrangement === 'direct'} value={c.rev_share_pct}
            onChange={(e) => updateChannel(c.id, { rev_share_pct: Number(e.target.value) || 0 })} /></div>
        <div className="fc-field"><label className="label">Payment terms, days</label>
          <input className="input mono" type="number" min={0} max={365} value={c.payment_terms_days}
            onChange={(e) => updateChannel(c.id, { payment_terms_days: Number(e.target.value) || 0 })} />
          <span className="fc-hint">0 = paid on sale. Drives the receivables working-capital drag.</span></div>
        <div className="fc-field"><label className="label">Active from</label>
          <select className="input" value={c.start_idx} onChange={(e) => updateChannel(c.id, { start_idx: Number(e.target.value) })}>
            {scenario.years.map((y, i) => <option key={i} value={i}>{y}</option>)}
          </select></div>
        <div className="fc-field"><label className="label">Retires</label>
          <select className="input" value={c.end_idx ?? ''} onChange={(e) => updateChannel(c.id, { end_idx: e.target.value === '' ? null : Number(e.target.value) })}>
            <option value="">No end (ongoing)</option>
            {scenario.years.map((y, i) => <option key={i} value={i}>{y}</option>)}
          </select></div>
      </div>
      <div className="fc-seg">
        <button className={c.vol_mode === 'manual' ? 'active' : ''} onClick={() => updateChannel(c.id, { vol_mode: 'manual' })}>Manual volumes</button>
        <button className={c.vol_mode === 'growth' ? 'active' : ''} onClick={() => updateChannel(c.id, { vol_mode: 'growth' })}>Growth curve</button>
      </div>
      {c.vol_mode === 'manual' ? (
        <div className="table-wrap"><table className="data fc-vol-table">
          <thead><tr><th>{scenario.unit_label}</th>{scenario.years.map((y) => <th key={y} className="num">{y}</th>)}</tr></thead>
          <tbody><tr><td /> {scenario.years.map((_, i) => {
            const activePeriod = i >= c.start_idx && (c.end_idx == null || i <= c.end_idx);
            return <td key={i} className="num">
              <input className="input mono" type="number" disabled={!activePeriod} value={activePeriod ? (c.manual_volumes[i] ?? 0) : ''}
                onChange={(e) => { const mv = [...c.manual_volumes]; mv[i] = Number(e.target.value) || 0; updateChannel(c.id, { manual_volumes: mv }); }} />
            </td>;
          })}</tr></tbody>
        </table></div>
      ) : (
        <>
          <div className="fc-field" style={{ maxWidth: 220 }}>
            <label className="label">Starting units, {scenario.years[c.start_idx]}</label>
            <input className="input mono" type="number" value={c.growth.base}
              onChange={(e) => updateChannel(c.id, { growth: { ...c.growth, base: Number(e.target.value) || 0 } })} />
          </div>
          <div className="table-wrap"><table className="data fc-vol-table">
            <thead><tr><th>Growth % vs prior period</th>{scenario.years.map((y) => <th key={y} className="num">{y}</th>)}</tr></thead>
            <tbody><tr><td /> {scenario.years.map((_, i) => (
              <td key={i} className="num">
                <input className="input mono" type="number" disabled={i <= c.start_idx} value={i <= c.start_idx ? '' : (c.growth.rates[i] ?? 0)}
                  onChange={(e) => { const r = [...c.growth.rates]; r[i] = Number(e.target.value) || 0; updateChannel(c.id, { growth: { ...c.growth, rates: r } }); }} />
              </td>
            ))}</tr></tbody>
          </table></div>
          <p className="fc-hint">Rate can vary every period: ramp, step down, or go negative to model decline.</p>
        </>
      )}
    </div>
  );
}

/* ---------------- Costs & funding ---------------- */

function CostsSection({ scenario, opex, equity, addOpexLine, updateOpexLine, removeOpexLine,
  addEquity, updateEquity, removeEquity, setArrayField, pullDate, setPullDate, pullLiveHeadcount,
  patchScenario, canToggle, sectionOverride, onToggle }: {
  scenario: Scenario; opex: OpexLine[]; equity: EquityRaise[];
  addOpexLine: () => void; updateOpexLine: (id: string, p: Partial<OpexLine>) => void; removeOpexLine: (id: string) => void;
  addEquity: () => void; updateEquity: (id: string, p: Partial<EquityRaise>) => void; removeEquity: (id: string) => void;
  setArrayField: (f: 'grants_by_period' | 'headcount' | 'cost_per_head', i: number, v: number) => void;
  pullDate: Record<number, string>; setPullDate: (f: (p: Record<number, string>) => Record<number, string>) => void;
  pullLiveHeadcount: (i: number) => void;
  patchScenario: (p: Partial<Scenario>) => void;
  canToggle: boolean; sectionOverride: Set<string>; onToggle: (key: string) => void;
}) {
  const N = scenario.years.length;
  const opexOn = sectionOverride.has('opex'), grantsOn = sectionOverride.has('grants'),
    equityOn = sectionOverride.has('equity'), peopleOn = sectionOverride.has('people');
  return (
    <div className="fc-section">
      <div className={`card fc-card ${opexOn ? 'fc-readonly' : ''}`}>
        <div className="fc-section-head"><h3>Operating expenses</h3>
          <div className="fc-btn-row"><SectionToggleBtn canToggle={canToggle} on={opexOn} onClick={() => onToggle('opex')} />
            <button className="btn ghost sm" onClick={addOpexLine}>+ Add line</button></div>
        </div>
        <p className="fc-sub">Tick Capex on a line that buys a depreciable asset rather than an ongoing cost: it drops out
          of the opex that reduces EBITDA, reduces cash directly when spent, and depreciates over the life below instead.</p>
        <div className="fc-field" style={{ maxWidth: 260, marginBottom: 12 }}>
          <label className="label">Depreciate capex over, years</label>
          <input className="input mono" type="number" min={1} value={scenario.capex_useful_life_years}
            onChange={(e) => patchScenario({ capex_useful_life_years: Math.max(1, Number(e.target.value) || 1) })} />
        </div>
        <div className="table-wrap"><table className="data fc-vol-table">
          <thead><tr><th>Category</th><th className="num">Capex</th>{scenario.years.map((y) => <th key={y} className="num">{y}</th>)}<th /></tr></thead>
          <tbody>
            {opex.map((o) => (
              <tr key={o.id}>
                <td><input className="input fc-name-input" value={o.name} onChange={(e) => updateOpexLine(o.id, { name: e.target.value })} /></td>
                <td className="num"><input type="checkbox" checked={o.is_capex}
                  onChange={(e) => updateOpexLine(o.id, { is_capex: e.target.checked })} /></td>
                {o.amounts.map((v, i) => (
                  <td key={i} className="num"><input className="input mono" type="number" value={v}
                    onChange={(e) => { const a = [...o.amounts]; a[i] = Number(e.target.value) || 0; updateOpexLine(o.id, { amounts: a }); }} /></td>
                ))}
                <td><button className="btn ghost sm" onClick={() => removeOpexLine(o.id)}>✕</button></td>
              </tr>
            ))}
            {opex.length === 0 && <tr><td colSpan={N + 3} className="fc-empty-row">No cost lines yet</td></tr>}
          </tbody>
        </table></div>
      </div>

      <div className={`card fc-card ${grantsOn ? 'fc-readonly' : ''}`}>
        <div className="fc-section-head"><h3>Non-dilutive grants</h3><SectionToggleBtn canToggle={canToggle} on={grantsOn} onClick={() => onToggle('grants')} /></div>
        <label className="fc-chair-toggle" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <input type="checkbox" checked={scenario.grants_committed}
            onChange={(e) => patchScenario({ grants_committed: e.target.checked })} />
          Committed (an actual award letter exists)
        </label>
        {!scenario.grants_committed && (
          <p className="fc-hint">Not committed: the amounts below are shown for reference only and are excluded from
            EBITDA and cash entirely, not netted in as a rounding adjustment.</p>
        )}
        {scenario.grants_committed && (
          <div className="fc-field" style={{ maxWidth: 260, marginBottom: 10 }}>
            <label className="label">Reimbursement lag, days</label>
            <input className="input mono" type="number" min={0} max={365} value={scenario.grant_lag_days}
              onChange={(e) => patchScenario({ grant_lag_days: Number(e.target.value) || 0 })} />
            <span className="fc-hint">Real schemes pay in arrears: cash lands this many days after the award period, not on award.</span>
          </div>
        )}
        <div className="table-wrap"><table className="data fc-vol-table">
          <thead><tr><th /> {scenario.years.map((y) => <th key={y} className="num">{y}</th>)}</tr></thead>
          <tbody><tr><td /> {scenario.grants_by_period.map((v, i) => (
            <td key={i} className="num"><input className="input mono" type="number" value={v}
              onChange={(e) => setArrayField('grants_by_period', i, Number(e.target.value) || 0)} /></td>
          ))}</tr></tbody>
        </table></div>
      </div>

      <div className={`card fc-card ${equityOn ? 'fc-readonly' : ''}`}>
        <div className="fc-section-head"><h3>Equity raises</h3>
          <div className="fc-btn-row"><SectionToggleBtn canToggle={canToggle} on={equityOn} onClick={() => onToggle('equity')} />
            <button className="btn ghost sm" onClick={addEquity}>+ Add round</button></div>
        </div>
        <div className="table-wrap"><table className="data">
          <thead><tr><th>Round</th><th>Lands</th><th className="num">Amount</th><th /></tr></thead>
          <tbody>
            {equity.map((e) => (
              <tr key={e.id}>
                <td><input className="input fc-name-input" value={e.name} onChange={(ev) => updateEquity(e.id, { name: ev.target.value })} /></td>
                <td><select className="input" value={e.period_idx} onChange={(ev) => updateEquity(e.id, { period_idx: Number(ev.target.value) })}>
                  {scenario.years.map((y, i) => <option key={i} value={i}>{y}</option>)}
                </select></td>
                <td className="num"><input className="input mono" type="number" value={e.amount}
                  onChange={(ev) => updateEquity(e.id, { amount: Number(ev.target.value) || 0 })} /></td>
                <td><button className="btn ghost sm" onClick={() => removeEquity(e.id)}>✕</button></td>
              </tr>
            ))}
            {equity.length === 0 && <tr><td colSpan={4} className="fc-empty-row">No raises modelled yet</td></tr>}
          </tbody>
        </table></div>
      </div>

      <div className={`card fc-card ${peopleOn ? 'fc-readonly' : ''}`}>
        <div className="fc-section-head"><h3>People</h3><SectionToggleBtn canToggle={canToggle} on={peopleOn} onClick={() => onToggle('people')} /></div>
        <p className="fc-sub">Headcount &times; cost per head drives employment cost automatically. Pull a period live from Crew, or enter it by hand.</p>
        <div className="table-wrap"><table className="data fc-vol-table">
          <thead><tr><th /> {scenario.years.map((y) => <th key={y} className="num">{y}</th>)}</tr></thead>
          <tbody>
            <tr><td className="fc-row-label">Headcount, year end</td>{scenario.headcount.map((v, i) => (
              <td key={i} className="num"><input className="input mono" type="number" value={v} onChange={(e) => setArrayField('headcount', i, Number(e.target.value) || 0)} /></td>
            ))}</tr>
            <tr><td className="fc-row-label">Cost per head</td>{scenario.cost_per_head.map((v, i) => (
              <td key={i} className="num"><input className="input mono" type="number" value={v} onChange={(e) => setArrayField('cost_per_head', i, Number(e.target.value) || 0)} /></td>
            ))}</tr>
            <tr className="fc-total"><td className="fc-row-label">People cost (computed)</td>{scenario.headcount.map((h, i) => (
              <td key={i} className="num mono">{fmt(scenario, (Number(h) || 0) * (Number(scenario.cost_per_head[i]) || 0))}</td>
            ))}</tr>
            <tr><td className="fc-row-label">Pull live</td>{scenario.years.map((_, i) => (
              <td key={i} className="num fc-pull-cell">
                <input className="input fc-pull-date" type="date" value={pullDate[i] ?? ''} onChange={(e) => setPullDate((p) => ({ ...p, [i]: e.target.value }))} />
                <button className="btn ghost sm" onClick={() => pullLiveHeadcount(i)}>Pull</button>
              </td>
            ))}</tr>
          </tbody>
        </table></div>
        {scenario.headcount_is_live && <p className="fc-hint">At least one period here was pulled from Crew's own records (aggregate headcount and cost only, no individual salary is ever shown).</p>}
      </div>
    </div>
  );
}

/* ---------------- P&L ---------------- */

function PLTableBody({ scenario, channels, opex, res }: { scenario: Scenario; channels: Channel[]; opex: OpexLine[]; res: ComputedModel }) {
  const N = scenario.years.length;
  const nonCapex = opex.filter((o) => !o.is_capex);
  const capexLines = opex.filter((o) => o.is_capex);
  return (
    <tbody>
      <tr className="fc-group"><td colSpan={N + 1}>Revenue by channel</td></tr>
      {channels.map((c) => <tr key={c.id}><td>{c.name}</td>{res.perChannel[c.id].rev.map((v, i) => <td key={i} className="num mono">{fmt(scenario, v)}</td>)}</tr>)}
      <tr className="fc-total"><td>Revenue</td>{res.totRev.map((v, i) => <td key={i} className="num mono">{fmt(scenario, v)}</td>)}</tr>
      <tr><td>less cost of goods</td>{res.totCogs.map((v, i) => <td key={i} className="num mono fc-neg">({fmt(scenario, v)})</td>)}</tr>
      <tr className="fc-total"><td>Gross profit</td>{res.gp.map((v, i) => <td key={i} className="num mono">{fmt(scenario, v)}</td>)}</tr>
      <tr><td>Gross margin</td>{res.gm.map((v, i) => <td key={i} className="num mono">{(v * 100).toFixed(0)}%</td>)}</tr>

      <tr className="fc-group"><td colSpan={N + 1}>Working capital</td></tr>
      <tr><td>Accounts receivable, period end</td>{res.ar.map((v, i) => <td key={i} className="num mono">{fmt(scenario, v)}</td>)}</tr>
      <tr><td>Change in receivables (cash drag when positive)</td>{res.deltaAR.map((v, i) => <td key={i} className="num mono">{fmt(scenario, v)}</td>)}</tr>

      <tr className="fc-group"><td colSpan={N + 1}>Operating expenses (excludes capex, below)</td></tr>
      <tr><td>People (headcount &times; cost per head)</td>{res.peopleCost.map((v, i) => <td key={i} className="num mono fc-neg">({fmt(scenario, v)})</td>)}</tr>
      {nonCapex.map((o) => <tr key={o.id}><td>{o.name}</td>{o.amounts.map((v, i) => <td key={i} className="num mono fc-neg">({fmt(scenario, v)})</td>)}</tr>)}
      <tr><td>Opex total</td>{res.opexTot.map((v, i) => <td key={i} className="num mono fc-neg">({fmt(scenario, v)})</td>)}</tr>
      <tr>
        <td>{scenario.grants_committed ? 'Grants (committed)' : 'Grants (memo only, not committed)'}</td>
        {res.grantsPL.map((v, i) => <td key={i} className="num mono">{scenario.grants_committed ? fmt(scenario, v) : `(${fmt(scenario, scenario.grants_by_period[i])})`}</td>)}
      </tr>
      {!scenario.grants_committed && <tr><td colSpan={N + 1} className="fc-hint" style={{ padding: '2px 8px 8px' }}>No award letter exists yet: excluded from EBITDA and cash, shown above in brackets for reference only.</td></tr>}
      <tr className="fc-total"><td>EBITDA</td>{res.ebitda.map((v, i) => <td key={i} className={`num mono ${v < 0 ? 'fc-neg' : 'fc-pos'}`}>{fmt(scenario, v)}</td>)}</tr>

      {capexLines.length > 0 && (
        <>
          <tr className="fc-group"><td colSpan={N + 1}>Capex and depreciation</td></tr>
          {capexLines.map((o) => <tr key={o.id}><td>{o.name}</td>{o.amounts.map((v, i) => <td key={i} className="num mono fc-neg">({fmt(scenario, v)})</td>)}</tr>)}
          <tr><td>Depreciation, {scenario.capex_useful_life_years}yr straight-line</td>{res.depreciation.map((v, i) => <td key={i} className="num mono fc-neg">({fmt(scenario, v)})</td>)}</tr>
          <tr className="fc-total"><td>EBIT</td>{res.ebit.map((v, i) => <td key={i} className={`num mono ${v < 0 ? 'fc-neg' : 'fc-pos'}`}>{fmt(scenario, v)}</td>)}</tr>
        </>
      )}

      <tr className="fc-group"><td colSpan={N + 1}>Funding &amp; cash</td></tr>
      <tr><td>Equity raised</td>{res.equity.map((v, i) => <td key={i} className="num mono">{fmt(scenario, v)}</td>)}</tr>
      <tr className="fc-total"><td>Cash (cumulative)</td>{res.cash.map((v, i) => <td key={i} className={`num mono ${v < 0 ? 'fc-neg' : ''}`}>{fmt(scenario, v)}</td>)}</tr>
    </tbody>
  );
}

function PLSection({ scenario, channels, opex, res }: { scenario: Scenario; channels: Channel[]; opex: OpexLine[]; res: ComputedModel }) {
  return (
    <div className="fc-section">
      <div className="card fc-card">
        <div className="table-wrap"><table className="data">
          <thead><tr><th>{scenario.currency} / period</th>{scenario.years.map((y) => <th key={y} className="num">{y}</th>)}</tr></thead>
          <PLTableBody scenario={scenario} channels={channels} opex={opex} res={res} />
        </table></div>
      </div>
    </div>
  );
}

/* ---------------- Sensitivity ---------------- */

const SENS_DELTAS = [-20, -10, 0, 10, 20];

function SensitivitySection({ scenario, channels, opex, equity, metric, setMetric, yearIdx, setYearIdx }: {
  scenario: Scenario; channels: Channel[]; opex: OpexLine[]; equity: EquityRaise[];
  metric: 'ebitda' | 'totRev' | 'cash'; setMetric: (m: 'ebitda' | 'totRev' | 'cash') => void;
  yearIdx: number; setYearIdx: (i: number) => void;
}) {
  const base = useMemo(() => computeUniform(scenario, channels, opex, equity, 1, 1), [scenario, channels, opex, equity]);
  const baseVal = metricValue(base, metric, yearIdx);

  if (channels.length === 0) {
    return <div className="fc-section"><SensControls scenario={scenario} metric={metric} setMetric={setMetric} yearIdx={yearIdx} setYearIdx={setYearIdx} />
      <div className="card fc-empty">Add revenue channels first to run a sensitivity analysis.</div></div>;
  }

  const cells = SENS_DELTAS.map((p) => SENS_DELTAS.map((v) => {
    const r = computeUniform(scenario, channels, opex, equity, 1 + p / 100, 1 + v / 100);
    return metricValue(r, metric, yearIdx);
  }));
  let extreme = 0.0001;
  cells.forEach((row) => row.forEach((val) => { extreme = Math.max(extreme, Math.abs(val - baseVal)); }));

  const impacts = channels.map((ch) => {
    const priceUp = metricValue(computeSingleChannelFlex(scenario, channels, opex, equity, ch.id, 'price', 1.2), metric, yearIdx) - baseVal;
    const priceDown = metricValue(computeSingleChannelFlex(scenario, channels, opex, equity, ch.id, 'price', 0.8), metric, yearIdx) - baseVal;
    const volUp = metricValue(computeSingleChannelFlex(scenario, channels, opex, equity, ch.id, 'vol', 1.2), metric, yearIdx) - baseVal;
    const volDown = metricValue(computeSingleChannelFlex(scenario, channels, opex, equity, ch.id, 'vol', 0.8), metric, yearIdx) - baseVal;
    return { ch, priceRange: [priceDown, priceUp] as [number, number], volRange: [volDown, volUp] as [number, number] };
  });
  let maxAbs = 0.0001;
  impacts.forEach((im) => { maxAbs = Math.max(maxAbs, ...im.priceRange.map(Math.abs), ...im.volRange.map(Math.abs)); });

  const metricLabel = metric === 'ebitda' ? 'EBITDA' : metric === 'totRev' ? 'revenue' : 'cash balance';

  return (
    <div className="fc-section">
      <SensControls scenario={scenario} metric={metric} setMetric={setMetric} yearIdx={yearIdx} setYearIdx={setYearIdx} />

      <div className="card fc-card">
        <h3>Price &times; volume grid</h3>
        <p className="fc-sub">Uniform adjustment applied across every channel, versus the base case at 0% / 0%.</p>
        <div className="fc-sens-grid">
          <div />
          {SENS_DELTAS.map((v) => <div key={v} className="fc-sens-hdr">vol {v > 0 ? '+' : ''}{v}%</div>)}
          {SENS_DELTAS.map((p, ri) => (
            <Fragment key={p}>
              <div className="fc-sens-hdr fc-sens-rowlbl">price {p > 0 ? '+' : ''}{p}%</div>
              {SENS_DELTAS.map((v, ci) => {
                const val = cells[ri][ci];
                const d = val - baseVal;
                const t = Math.min(1, Math.abs(d) / extreme);
                const isBase = p === 0 && v === 0;
                const bg = isBase ? 'var(--surface-2)' : d >= 0
                  ? `color-mix(in srgb, var(--ok) ${Math.round(t * 65)}%, var(--surface))`
                  : `color-mix(in srgb, var(--risk) ${Math.round(t * 65)}%, var(--surface))`;
                return <div key={`${p}-${v}`} className="fc-sens-cell mono" style={{ background: bg, border: isBase ? '1.5px solid var(--text-3)' : undefined }}>{fmtCompact(scenario, val)}</div>;
              })}
            </Fragment>
          ))}
        </div>
        <p className="fc-hint">Base case (0% / 0%) = {fmt(scenario, baseVal)} in {scenario.years[yearIdx]}.</p>
      </div>

      <div className="card fc-card">
        <h3>By channel, &plusmn;20%</h3>
        <p className="fc-sub">Impact of a 20% move in that channel's price or volume alone, other channels held at base case.</p>
        {impacts.map((im) => (
          <div key={im.ch.id}>
            <TornadoRow label={`${im.ch.name} - price`} lo={im.priceRange[0]} hi={im.priceRange[1]} maxAbs={maxAbs} />
            <TornadoRow label="volume" lo={im.volRange[0]} hi={im.volRange[1]} maxAbs={maxAbs} />
          </div>
        ))}
        <p className="fc-hint">Bars show the swing in {metricLabel} for {scenario.years[yearIdx]} across a &plusmn;20% move, centred on the base case.</p>
      </div>
    </div>
  );
}

function SensControls({ scenario, metric, setMetric, yearIdx, setYearIdx }: {
  scenario: Scenario; metric: 'ebitda' | 'totRev' | 'cash'; setMetric: (m: 'ebitda' | 'totRev' | 'cash') => void;
  yearIdx: number; setYearIdx: (i: number) => void;
}) {
  return (
    <div className="card fc-card fc-field-grid">
      <div className="fc-field"><label className="label">Metric</label>
        <select className="input" value={metric} onChange={(e) => setMetric(e.target.value as any)}>
          <option value="ebitda">EBITDA</option><option value="totRev">Revenue</option><option value="cash">Cash balance</option>
        </select></div>
      <div className="fc-field"><label className="label">Period</label>
        <select className="input" value={yearIdx} onChange={(e) => setYearIdx(Number(e.target.value))}>
          {scenario.years.map((y, i) => <option key={i} value={i}>{y}</option>)}
        </select></div>
    </div>
  );
}

function TornadoRow({ label, lo, hi, maxAbs }: { label: string; lo: number; hi: number; maxAbs: number }) {
  const loP = 50 + (lo / maxAbs) * 48, hiP = 50 + (hi / maxAbs) * 48;
  const left = Math.min(loP, hiP), width = Math.abs(hiP - loP);
  return (
    <div className="fc-tornado-row">
      <div className="fc-tornado-label">{label}</div>
      <div className="fc-tornado-track">
        <div className="fc-tornado-mid" />
        <div className="fc-tornado-bar" style={{ left: `${left}%`, width: `${width}%` }} />
      </div>
    </div>
  );
}

/* ---------------- Compare scenarios ---------------- */

const CASE_ORDER: CaseType[] = ['base', 'upside', 'downside', 'other'];

function scenarioMetrics(sc: Scenario, kids: { channels: Channel[]; opex: OpexLine[]; equity: EquityRaise[] }) {
  const res = computeModel(sc, kids.channels, kids.opex, kids.equity);
  const N = sc.years.length;
  const equityTotal = res.equity.reduce((a, b) => a + b, 0);
  return { res, N, termRev: res.totRev[N - 1], termEbitda: res.ebitda[N - 1], termGM: res.gm[N - 1],
    breakEven: breakEvenYear(sc, res), minCash: res.minCash, equityTotal };
}
type ScenarioSummary = ReturnType<typeof scenarioMetrics> & { scenario: Scenario };

function CompareSection({ scenarios, childrenCache, ensureChildrenLoaded, patchScenarioById, deriveScenario }: {
  scenarios: Scenario[]; childrenCache: Record<string, { channels: Channel[]; opex: OpexLine[]; equity: EquityRaise[] }>;
  ensureChildrenLoaded: (id: string) => Promise<{ channels: Channel[]; opex: OpexLine[]; equity: EquityRaise[] }>;
  patchScenarioById: (id: string, p: Partial<Scenario>) => void;
  deriveScenario: (kind: 'upside' | 'downside') => void;
}) {
  useEffect(() => { scenarios.forEach((s) => { if (!childrenCache[s.id]) ensureChildrenLoaded(s.id); }); }, [scenarios, childrenCache, ensureChildrenLoaded]);

  const ordered = [...scenarios].sort((a, b) => CASE_ORDER.indexOf(a.case_type) - CASE_ORDER.indexOf(b.case_type));
  const ready = ordered.filter((s) => childrenCache[s.id]);
  const hasBase = scenarios.some((s) => s.case_type === 'base');
  const hasUpside = scenarios.some((s) => s.case_type === 'upside');
  const hasDownside = scenarios.some((s) => s.case_type === 'downside');

  if (ready.length < ordered.length) return <div className="fc-section"><div className="card fc-empty">Loading every scenario…</div></div>;

  const summaries = ready.map((s) => ({ scenario: s, ...scenarioMetrics(s, childrenCache[s.id]) }));

  return (
    <div className="fc-section">
      <div className="card fc-card">
        <div className="fc-section-head">
          <div><h3>Quick scenarios</h3><p className="fc-sub">Derives a copy of the base case with every channel's volume scaled &plusmn;20% from the second period onward, then tags it. Edit freely afterwards.</p></div>
          <div className="fc-btn-row">
            {!hasUpside && <button className="btn sm" disabled={!hasBase} onClick={() => deriveScenario('upside')}>+ Create upside</button>}
            {!hasDownside && <button className="btn sm" disabled={!hasBase} onClick={() => deriveScenario('downside')}>+ Create downside</button>}
          </div>
        </div>
        {!hasBase && <p className="fc-hint">Tag one scenario as Base case below before deriving an upside or downside from it.</p>}
      </div>

      <div className="card fc-card">
        <h3>Key metrics</h3>
        <div className="table-wrap"><table className="data">
          <thead><tr><th>Scenario</th><th>Case</th><th className="num">Terminal revenue</th><th className="num">Terminal EBITDA</th>
            <th className="num">Terminal GM</th><th>Break-even</th><th className="num">Cash low</th><th className="num">Equity raised</th></tr></thead>
          <tbody>
            {summaries.map((s) => (
              <tr key={s.scenario.id}>
                <td><span className={`case-dot case-${s.scenario.case_type}`} /> {s.scenario.name}</td>
                <td><select className="input fc-case-select" value={s.scenario.case_type}
                  onChange={(e) => patchScenarioById(s.scenario.id, { case_type: e.target.value as CaseType })}>
                  {CASE_ORDER.map((k) => <option key={k} value={k}>{CASE_LABEL[k]}</option>)}
                </select></td>
                <td className="num mono">{fmt(s.scenario, s.termRev)}</td>
                <td className={`num mono ${s.termEbitda < 0 ? 'fc-neg' : 'fc-pos'}`}>{fmt(s.scenario, s.termEbitda)}</td>
                <td className="num mono">{(s.termGM * 100).toFixed(0)}%</td>
                <td>{s.breakEven}</td>
                <td className={`num mono ${s.minCash < 0 ? 'fc-neg' : ''}`}>{fmt(s.scenario, s.minCash)}</td>
                <td className="num mono">{fmt(s.scenario, s.equityTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      <div className="card fc-card">
        <h3>Terminal revenue and EBITDA</h3>
        <p className="fc-sub">Grouped by scenario.</p>
        <CompareChart summaries={summaries} />
        <div className="fc-legend">
          <span className="fc-legend-item"><span className="fc-legend-swatch" style={{ background: 'var(--text-3)' }} />Revenue</span>
          <span className="fc-legend-item"><span className="fc-legend-swatch" style={{ background: 'var(--info, #3E7CB1)' }} />EBITDA</span>
        </div>
      </div>
    </div>
  );
}

function CompareChart({ summaries }: { summaries: ScenarioSummary[] }) {
  if (summaries.length === 0) return <div className="fc-empty">No scenarios yet.</div>;
  const W = 900, H = 260, padL = 60, padB = 34, padT = 14, padR = 14;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const allVals = summaries.flatMap((s) => [s.termRev, s.termEbitda]);
  const maxV = Math.max(1, ...allVals), minV = Math.min(0, ...allVals);
  const top = niceCeil(maxV), bottom = minV < 0 ? -niceCeil(-minV) : 0;
  const range = (top - bottom) || 1;
  const y = (v: number) => padT + plotH - ((v - bottom) / range) * plotH;
  const zeroY = y(0);
  const groupW = plotW / summaries.length;
  const barW = Math.min(46, groupW * 0.28);
  const ticks = [0, 1, 2, 3, 4].map((s) => bottom + range * s / 4);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Terminal revenue and EBITDA by scenario">
      {ticks.map((v, i) => (
        <g key={i}>
          <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="var(--border)" />
          <text x={padL - 8} y={y(v) + 4} textAnchor="end" fontSize="10.5" fill="var(--text-3)">{fmtCompact(summaries[0].scenario, v)}</text>
        </g>
      ))}
      {summaries.map((s, i) => {
        const cx = padL + groupW * i + groupW / 2;
        return (
          <g key={s.scenario.id}>
            {[['termRev', 'var(--text-3)'], ['termEbitda', 'var(--info, #3E7CB1)']].map(([key, color], j) => {
              const v = (s as any)[key];
              const x0 = cx - barW - 3 + j * (barW + 6);
              const yTop = Math.min(y(v), zeroY), h = Math.abs(y(v) - zeroY);
              return <rect key={key} x={x0} y={yTop} width={barW} height={Math.max(1, h)} rx={3} fill={color}><title>{s.scenario.name}: {fmt(s.scenario, v)}</title></rect>;
            })}
            <text x={cx} y={H - 10} textAnchor="middle" fontSize="10.5" fill="var(--text-2)">{CASE_LABEL[s.scenario.case_type]}</text>
          </g>
        );
      })}
      {bottom < 0 && <line x1={padL} x2={W - padR} y1={zeroY} y2={zeroY} stroke="var(--text-3)" strokeWidth="1.2" />}
    </svg>
  );
}

/* ---------------- Print ---------------- */

function PrintReport({ scenario, channels, opex, res }: { scenario: Scenario; channels: Channel[]; opex: OpexLine[]; res: ComputedModel }) {
  const N = scenario.years.length;
  const genDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div className="fc-print-only">
      <div className="fc-print-eyebrow">Glidepath by Patient Thread</div>
      <h1>{scenario.name}</h1>
      <p className="fc-sub">Prepared {genDate}. {scenario.notes}</p>
      <p className="fc-print-warning">Indicative founder-planning model. Not audited, not a forecast, not investment advice.</p>

      <div className="fc-stat-row">
        <div className="fc-stat"><div className="label">Revenue, {scenario.years[N - 1]}</div><div className="fc-stat-val mono">{fmtCompact(scenario, res.totRev[N - 1])}</div></div>
        <div className="fc-stat"><div className="label">EBITDA, {scenario.years[N - 1]}</div><div className="fc-stat-val mono">{fmtCompact(scenario, res.ebitda[N - 1])}</div></div>
        <div className="fc-stat"><div className="label">Break-even</div><div className="fc-stat-val">{breakEvenYear(scenario, res)}</div></div>
        <div className="fc-stat"><div className="label">Cash low point</div><div className="fc-stat-val mono">{fmtCompact(scenario, res.minCash)}</div></div>
      </div>

      <h2>P&amp;L and cash flow</h2>
      <table className="data">
        <thead><tr><th>{scenario.currency} / period</th>{scenario.years.map((y) => <th key={y} className="num">{y}</th>)}</tr></thead>
        <PLTableBody scenario={scenario} channels={channels} opex={opex} res={res} />
      </table>

      <p className="fc-print-footer">Generated by Glidepath by Patient Thread, {genDate}. Figures are founder-planning estimates only.</p>
    </div>
  );
}
